import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";

import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import {
  brandAddState,
  brandEditState,
  brandFilterText,
  brandRowForEdit,
  brandSaving,
  brandSelectedRows,
} from "../atoms/atoms";
import { IOfficeAppliancesBrand } from "../type";
import useAddOfficeAppliancesBrand from "../hooks/useAddOfficeAppliancesBrand";
import useUpdateOfficeAppliancesBrand from "../hooks/useUpdateOfficeAppliancesBrand";
import SubmitHookFormTS from "components/SubmitHookFormTS";
import OfficeAppliancesBrandDetails from "./OfficeAppliancesBrandDetails";

interface OfficeAppliancesBrandFormProps {
  setRecent: React.Dispatch<React.SetStateAction<string>>;
}

const OfficeAppliancesBrandForm: React.FC<OfficeAppliancesBrandFormProps> = ({
  setRecent,
}) => {
  const [editData, setEditData] = useRecoilState(brandRowForEdit);
  const [isSaving, setIsSaving] = useRecoilState(brandSaving);
  const [isAddState, setIsAddState] = useRecoilState(brandAddState);
  const [isEditState, setIsEditState] = useRecoilState(brandEditState);
  const [, setSelectedRows] = useRecoilState(brandSelectedRows);
  const addBrand = useAddOfficeAppliancesBrand();
  const updateBrand = useUpdateOfficeAppliancesBrand();
  const [newData, setNewData] = useState<IOfficeAppliancesBrand>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);
  const [, setFilterTextState] = useRecoilState(brandFilterText);

  const formMethods = useForm<IOfficeAppliancesBrand>({
    defaultValues: (editData as IOfficeAppliancesBrand) || {
      officeApplianceBrandId: 0,
      officeApplianceBrand1: "",
      createdBy: "",
      createdDate: "",
      modifiedBy: "",
      modifiedDate: "",
      isActive: true,
    },
  });

  const onSubmit = (data: IOfficeAppliancesBrand) => {
    const trimmedName = (data.officeApplianceBrand1 || "").trim();

    const base: IOfficeAppliancesBrand =
      (editData as IOfficeAppliancesBrand) || {
        officeApplianceBrandId: 0,
        createdBy: "",
        createdDate: new Date(),
        modifiedBy: "",
        modifiedDate: new Date(),
        isActive: true,
        officeApplianceBrand1: "",
      };

    const value: IOfficeAppliancesBrand = {
      ...base,
      ...data,
      officeApplianceBrandId: base.officeApplianceBrandId,
      officeApplianceBrand1: trimmedName,
    };

    setNewData(value);
    setSaveModal(true);
    setIsSaving(true);
  };

  const resetFormState = () => {
    formMethods.reset();
    formMethods.clearErrors();
    setEditData(null);
    setIsAddState(false);
    setIsEditState(false);
    setSelectedRows([]);
    setChanges(false);
  };

  const handleCancel = () => {
    resetFormState();
    setRecent("");
  };

  const handleSave = () => {
    if (!newData) return;
    if (isEditState) {
      updateBrand(newData);
    } else {
      addBrand(newData);
    }

    setFilterTextState("");
    setRecent("");

    resetFormState();
  };

  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Brand" : "Edit Brand"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookFormTS
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>

            <div className="mx-2 border-b-2 border-secondary" />
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b>Note:</b> All fields with * are required to fill.
            </div>

            <div className="mt-2">
              <OfficeAppliancesBrandDetails
                data={editData as IOfficeAppliancesBrand | undefined}
                setChanges={setChanges}
              />
            </div>
          </form>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add" : "update"
          } this data?`}
          ModalTitle={isAddState ? "Add Brand" : "Update Brand"}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
            setIsSaving(false);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default OfficeAppliancesBrandForm;
